// src/pages/PlacementRegister.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const ACCENT = "#1ECCB0";
const NAVY = "#0F1E3D";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  password: "",
  parentId: "",
  position: "LEFT",
  userType: "TRAINEE_ENTREPRENEUR",
};

export default function PlacementRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const previewTimer = useRef(null);

  const set = (key, val) => {
    setErr("");
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  // Auto-fetch preview when sponsorId + position are ready
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);

    const id = form.parentId.trim();
    if (!id || id.length < 3) {
      setPreview(null);
      setPreviewErr("");
      return;
    }

    previewTimer.current = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        setPreviewErr("");
        const res = await axiosInstance.get(
          `/api/auth/placement-preview?sponsorId=${id}&position=${form.position}`
        );
        setPreview(res?.data || null);
      } catch (e) {
        setPreview(null);
        setPreviewErr(
          e?.response?.data?.message ||
          e?.response?.data?.msg ||
          "Could not fetch placement preview."
        );
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(previewTimer.current);
  }, [form.parentId, form.position]);

  const validate = () => {
    if (!form.name.trim()) return "Full name is required.";
    if (!form.email.trim()) return "Email is required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email address.";
    if (!form.phone.trim()) return "Phone number is required.";
    if (!/^\d{10}$/.test(form.phone.trim())) return "Phone number must be exactly 10 digits.";
    if (!form.password.trim()) return "Password is required.";
    if (form.password.length < 6) return "Password must be at least 6 characters.";
    if (!form.parentId.trim()) return "Parent / Sponsor ID is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errMsg = validate();
    if (errMsg) { setErr(errMsg); return; }

    try {
      setLoading(true);
      setErr("");
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        parentId: form.parentId.trim(),
        position: form.position,
        userType: form.userType,
      };
      const res = await axiosInstance.post("/api/auth/placement-register", payload);
      setSuccess(res?.data || { message: "Registration successful!" });
    } catch (e) {
      setErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setErr("");
    setSuccess(null);
    setPreview(null);
    setPreviewErr("");
  };

  // ── SUCCESS SCREEN ──
  if (success) {
    return (
      <div className="pr-page">
        <div className="pr-success-wrap">
          <div className="pr-success-card">
            <div className="pr-success-icon">✓</div>
            <h2 className="pr-success-title">Registration Successful!</h2>
            <p className="pr-success-sub">
              {success?.message || success?.msg || "The member has been registered successfully."}
            </p>

            {(success?.user || success?.data) && (
              <div className="pr-success-details">
                {[
                  ["Name", (success.user || success.data)?.name],
                  ["User ID", (success.user || success.data)?.userID || (success.user || success.data)?.userId],
                  ["Mobile", (success.user || success.data)?.phone],
                  ["Email", (success.user || success.data)?.email],
                  ["User Type", form.userType.replace(/_/g, " ")],
                  ["Referred By", success.placement ? `${success.placement.parentName || (success.user || success.data)?.referralCode || ""} (${success.placement.parentId || ""})` : null],
                  ["Placed Under", success.placement?.placedUnderUserID || success.placement?.parentId || null],
                  ["Position", success.placement?.position || form.position],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="pr-detail-row">
                      <span className="pr-detail-key">{k}</span>
                      <span className="pr-detail-val">{v}</span>
                    </div>
                  ))}
              </div>
            )}

            <div className="pr-success-btns">
              <button className="pr-btn-outline" onClick={handleReset}>
                + Register Another
              </button>
              <button className="pr-btn-primary" onClick={() => navigate(-1)}>
                ← Go Back
              </button>
            </div>
          </div>
        </div>

        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="pr-page">
      <style>{styles}</style>

      {/* TOP BAR */}
      <div className="pr-topbar">
        <button className="pr-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <span className="pr-topbar-title">Register Member</span>
        <span style={{ width: 70 }} />
      </div>

      {/* FORM CARD */}
      <div className="pr-wrap">
        <div className="pr-card">

          {/* Card Header */}
          <div className="pr-card-header">
            <h1 className="pr-card-title">New Member Registration</h1>
            <p className="pr-card-sub">Fill in all required details to register a new placement member.</p>
          </div>

          {/* Error */}
          {err && (
            <div className="pr-error">
              <span>⚠</span> {err}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            {/* ── Personal Information ── */}
            <SectionLabel label="Personal Information" />

            <Field
              label="Full Name"
              required
              placeholder="Enter full name"
              value={form.name}
              onChange={(v) => set("name", v)}
            />
            <Field
              label="Email Address"
              required
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={(v) => set("email", v)}
            />
            <Field
              label="Phone Number"
              required
              type="tel"
              placeholder="Enter 10 digit phone number"
              value={form.phone}
              onChange={(v) => set("phone", String(v).replace(/\D/g, "").slice(0, 10))}
            />

            {/* Password */}
            <div className="pr-field">
              <label className="pr-label">
                Password <span className="pr-req">*</span>
              </label>
              <div className="pr-pass-wrap">
                <input
                  className="pr-input"
                  type={showPass ? "text" : "password"}
                  placeholder="Minimum 6 characters"
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pr-eye-btn"
                  onClick={() => setShowPass((p) => !p)}
                  tabIndex={-1}
                >
                  {showPass ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* ── Placement Information ── */}
            <SectionLabel label="Placement Information" />

            {/* Sponsor / Parent ID */}
            <Field
              label="Parent / Sponsor ID"
              required
              placeholder="e.g. S573040"
              value={form.parentId}
              onChange={(v) => set("parentId", v)}
            />

            {/* Position Selector */}
            <div className="pr-field">
              <label className="pr-label">
                Position <span className="pr-req">*</span>
              </label>
              <div className="pr-pos-row">
                {["LEFT", "RIGHT"].map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    className={`pr-pos-btn${form.position === pos ? " active" : ""}`}
                    onClick={() => set("position", pos)}
                  >
                    <span className="pr-pos-arrow">{pos === "LEFT" ? "◀" : "▶"}</span>
                    <span>{pos}</span>
                    {form.position === pos && <span className="pr-pos-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Placement Preview ── */}
            {(previewLoading || preview || previewErr) && (
              <div className="pr-preview-box">
                <div className="pr-preview-header">
                  <span className="pr-preview-label">Placement Preview</span>
                  {previewLoading && <span className="pr-preview-spinner" />}
                </div>

                {previewErr && (
                  <div className="pr-preview-err">{previewErr}</div>
                )}

                {preview && !previewLoading && (
                  <div className="pr-preview-grid">
                    {[
                      ["Sponsor ID", preview.sponsorId],
                      ["Sponsor Name", preview.sponsorName],
                      ["Placement Parent ID", preview.placementParentId],
                      ["Placement Parent Name", preview.placementParentName],
                      ["Position", preview.position],
                    ]
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <div key={k} className="pr-preview-row">
                          <span className="pr-preview-key">{k}</span>
                          <span className="pr-preview-val">{v}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* SUBMIT */}
            <button
              type="submit"
              className={`pr-submit${loading ? " disabled" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <span className="pr-submit-inner">
                  <span className="pr-spinner" /> Registering…
                </span>
              ) : (
                <span className="pr-submit-inner">Register Member</span>
              )}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}

/* ── Reusable field ── */
function Field({ label, required, placeholder, value, onChange, type = "text" }) {
  return (
    <div className="pr-field">
      <label className="pr-label">
        {label} {required && <span className="pr-req">*</span>}
      </label>
      <input
        className="pr-input"
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
    </div>
  );
}

/* ── Section label ── */
function SectionLabel({ label }) {
  return (
    <div className="pr-section-label">
      <div className="pr-section-line" />
      <span className="pr-section-text">{label}</span>
      <div className="pr-section-line" />
    </div>
  );
}

/* ════════ STYLES ════════ */
const styles = `
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #root { min-height: 100%; }
  body {
    font-family: 'Segoe UI', ui-sans-serif, system-ui, sans-serif;
    background: #f0f2f5;
    color: #0f172a;
  }
  input, button, select, textarea { font-family: inherit; }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes popIn {
    from { opacity: 0; transform: scale(.94); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; } 50% { opacity: .5; }
  }

  /* PAGE */
  .pr-page {
    min-height: 100vh;
    background: #f0f2f5;
  }

  /* TOPBAR */
  .pr-topbar {
    background: ${NAVY};
    padding: 0 20px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 12px rgba(0,0,0,.22);
  }
  .pr-back-btn {
    height: 34px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,.2);
    background: rgba(255,255,255,.08);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.2px;
    transition: background .15s;
  }
  .pr-back-btn:hover { background: rgba(255,255,255,.16); }
  .pr-topbar-title {
    font-size: 15px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.3px;
  }

  /* WRAP */
  .pr-wrap {
    max-width: 600px;
    margin: 32px auto;
    padding: 0 16px 48px;
    animation: fadeUp .38s ease both;
  }

  /* CARD */
  .pr-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(0,0,0,.07);
    box-shadow: 0 4px 24px rgba(0,0,0,.07);
    padding: 28px 28px 32px;
  }

  /* CARD HEADER */
  .pr-card-header {
    margin-bottom: 4px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
  }
  .pr-card-title {
    font-size: 20px;
    font-weight: 900;
    color: ${NAVY};
    letter-spacing: -0.3px;
  }
  .pr-card-sub {
    font-size: 13px;
    color: #64748b;
    margin-top: 5px;
    line-height: 1.5;
  }

  /* ERROR */
  .pr-error {
    margin: 16px 0;
    padding: 12px 16px;
    border-radius: 10px;
    background: rgba(239,68,68,.07);
    border: 1px solid rgba(239,68,68,.22);
    color: #b91c1c;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.5;
  }

  /* SECTION LABEL */
  .pr-section-label {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 22px 0 14px;
  }
  .pr-section-line {
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
  .pr-section-text {
    font-size: 10.5px;
    font-weight: 800;
    color: #94a3b8;
    letter-spacing: 1.3px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  /* FIELD */
  .pr-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
  }
  .pr-label {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.1px;
  }
  .pr-req { color: #ef4444; margin-left: 2px; }
  .pr-input {
    width: 100%;
    height: 46px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    transition: border-color .18s, box-shadow .18s;
    outline: none;
  }
  .pr-input:focus {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px rgba(30,204,176,.12);
    background: #fff;
  }
  .pr-input::placeholder { color: #b0bec5; font-weight: 400; }

  /* PASSWORD */
  .pr-pass-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .pr-pass-wrap .pr-input {
    padding-right: 72px;
  }
  .pr-eye-btn {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    padding: 4px 6px;
    border-radius: 6px;
    letter-spacing: 0.3px;
    transition: color .15s;
  }
  .pr-eye-btn:hover { color: ${NAVY}; }

  /* POSITION */
  .pr-pos-row {
    display: flex;
    gap: 12px;
  }
  .pr-pos-btn {
    flex: 1;
    height: 48px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 800;
    font-size: 14px;
    color: #64748b;
    letter-spacing: 0.5px;
    transition: all .18s;
    position: relative;
  }
  .pr-pos-btn:hover {
    border-color: #cbd5e1;
    background: #f1f5f9;
  }
  .pr-pos-btn.active {
    border-color: ${ACCENT};
    background: rgba(30,204,176,.07);
    color: #0a6b5c;
    box-shadow: 0 0 0 3px rgba(30,204,176,.12);
  }
  .pr-pos-arrow { font-size: 12px; }
  .pr-pos-check {
    position: absolute;
    top: 5px;
    right: 9px;
    font-size: 11px;
    color: ${ACCENT};
    font-weight: 900;
  }

  /* PREVIEW BOX */
  .pr-preview-box {
    margin: 6px 0 18px;
    border-radius: 12px;
    border: 1.5px solid rgba(30,204,176,.3);
    background: rgba(30,204,176,.04);
    overflow: hidden;
  }
  .pr-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: rgba(30,204,176,.08);
    border-bottom: 1px solid rgba(30,204,176,.15);
  }
  .pr-preview-label {
    font-size: 11px;
    font-weight: 800;
    color: #0a6b5c;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .pr-preview-spinner {
    width: 14px; height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(30,204,176,.3);
    border-top-color: ${ACCENT};
    animation: spin .6s linear infinite;
    display: inline-block;
  }
  .pr-preview-err {
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 600;
    color: #b91c1c;
  }
  .pr-preview-grid {
    padding: 4px 0;
  }
  .pr-preview-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 9px 14px;
    border-bottom: 1px solid rgba(30,204,176,.1);
    gap: 12px;
  }
  .pr-preview-row:last-child { border-bottom: none; }
  .pr-preview-key {
    font-size: 12.5px;
    font-weight: 600;
    color: #64748b;
    flex-shrink: 0;
    min-width: 150px;
  }
  .pr-preview-val {
    font-size: 13px;
    font-weight: 800;
    color: ${NAVY};
    text-align: right;
  }

  /* SUBMIT */
  .pr-submit {
    margin-top: 24px;
    width: 100%;
    height: 50px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, ${NAVY} 0%, #1a3a6e 100%);
    color: #fff;
    font-weight: 800;
    font-size: 15px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(15,30,61,.28);
    transition: opacity .18s, transform .14s, box-shadow .18s;
    letter-spacing: 0.3px;
  }
  .pr-submit:hover:not(.disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(15,30,61,.34);
  }
  .pr-submit:active:not(.disabled) { transform: translateY(0); }
  .pr-submit.disabled { opacity: 0.62; cursor: not-allowed; }
  .pr-submit-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
  .pr-spinner {
    width: 17px; height: 17px;
    border-radius: 50%;
    border: 2.5px solid rgba(255,255,255,.3);
    border-top-color: #fff;
    animation: spin .7s linear infinite;
    display: inline-block;
  }

  /* SUCCESS */
  .pr-success-wrap {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: #f0f2f5;
  }
  .pr-success-card {
    background: #fff;
    border-radius: 20px;
    border: 1px solid rgba(0,0,0,.07);
    box-shadow: 0 12px 40px rgba(0,0,0,.10);
    padding: 36px 28px;
    max-width: 460px;
    width: 100%;
    text-align: center;
    animation: popIn .32s ease both;
  }
  .pr-success-icon {
    width: 64px; height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${ACCENT} 0%, #12b89a 100%);
    display: grid;
    place-items: center;
    font-size: 28px;
    color: #fff;
    margin: 0 auto 18px;
    box-shadow: 0 8px 22px rgba(30,204,176,.35);
  }
  .pr-success-title {
    font-size: 21px;
    font-weight: 900;
    color: ${NAVY};
    margin-bottom: 8px;
  }
  .pr-success-sub {
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 20px;
  }
  .pr-success-details {
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    margin-bottom: 24px;
    text-align: left;
  }
  .pr-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
    gap: 12px;
  }
  .pr-detail-row:last-child { border-bottom: none; }
  .pr-detail-key { font-size: 13px; font-weight: 600; color: #64748b; }
  .pr-detail-val { font-size: 13px; font-weight: 800; color: ${NAVY}; }
  .pr-success-btns { display: flex; gap: 12px; }
  .pr-btn-outline {
    flex: 1; height: 44px;
    border-radius: 10px;
    border: 1.5px solid ${ACCENT};
    background: rgba(30,204,176,.07);
    color: #0a6b5c;
    font-weight: 800;
    font-size: 14px;
    cursor: pointer;
    transition: background .15s;
  }
  .pr-btn-outline:hover { background: rgba(30,204,176,.13); }
  .pr-btn-primary {
    flex: 1; height: 44px;
    border-radius: 10px;
    border: none;
    background: ${NAVY};
    color: #fff;
    font-weight: 800;
    font-size: 14px;
    cursor: pointer;
    transition: opacity .15s;
  }
  .pr-btn-primary:hover { opacity: 0.87; }

  /* MOBILE */
  @media (max-width: 500px) {
    .pr-wrap { margin: 16px auto; padding: 0 10px 40px; }
    .pr-card { padding: 20px 16px 24px; border-radius: 14px; }
    .pr-card-title { font-size: 17px; }
    .pr-topbar { padding: 0 14px; }
    .pr-topbar-title { font-size: 14px; }
    .pr-pos-btn { height: 44px; font-size: 13px; }
    .pr-input { height: 44px; font-size: 13px; }
    .pr-submit { height: 48px; font-size: 14px; }
    .pr-preview-key { min-width: 120px; font-size: 12px; }
    .pr-preview-val { font-size: 12px; }
    .pr-success-card { padding: 28px 18px; }
    .pr-success-btns { flex-direction: row; gap: 8px; }
    .pr-btn-outline, .pr-btn-primary { font-size: 13px; padding: 0 4px; height: 44px; display: flex; align-items: center; justify-content: center; text-align: center; }
  }
`;